import React, { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import clsx from "clsx";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Divider, Form, Spin, Steps } from "antd";
import styled from "styled-components";
import { getContent, toggleSpinner, editContent, setContent, getOpportunity, getProgramById } from "../../redux/actions";
import StyledText from "../../components/StyledText";
import Button from "../../components/Button";
import { getLocalStorageItem } from "../../utils/utilities";
import {
    toggleSpinnerSelector,
    opportunityDataSelector,
    savedContentListSelector,
    savedContentDataSelector,
    programDataSelector
} from "../../redux/selectors";
import {
    useAppDispatch,
    useAppSelector
} from "../../redux/store";
import 'react-quill/dist/quill.snow.css';

const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 12 },
};

const CharityFinalNextSteps = ({ className }) => {
    const baseClassName = clsx("charityFinalNextSteps", className);
    const intl = useIntl();
    const [formData, setFormData] = useState({});
    const [savedClicked, setSavedClicked] = useState(false);
    const [whatHappensNext, setWhatHappensNext] = useState('');
    const [additionalComment, setAdditionalComment] = useState('');
    const [whatHappensNextError, setWhatHappensNextError] = useState(false);
    const [additionalCommentError, setAdditionalCommentError] = useState(false);
    const opportunityData = useAppSelector(opportunityDataSelector) || {};
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const showSpinner = useAppSelector(toggleSpinnerSelector);
    const programData = useAppSelector(programDataSelector);
    const { customContentQuestion } = programData || {};
    const { ID } = useParams();
    const {
        ID: contentId,
        whatHappensNext: savedWhatHappensNext,
        additionalComment: savedAdditionalComment
    } = useAppSelector(savedContentDataSelector) || {};
    const { opportunityID } = JSON.parse(getLocalStorageItem('contentData') || '{}');
    const { opportunity: { programID } = {} } = opportunityData || {};
    const { message } = useAppSelector(savedContentListSelector) || {};
    const maxChars = 500;

    useEffect(() => {
        if (contentId) {
            const data = {
                ...(savedWhatHappensNext && { whatHappensNext: savedWhatHappensNext }),
                ...(savedAdditionalComment && { additionalComment: savedAdditionalComment }),
            };
            form.setFieldsValue(data);
            setFormData(data);
            setWhatHappensNext(savedWhatHappensNext || '');
            setAdditionalComment(savedAdditionalComment || '');
        }
    }, [contentId, savedWhatHappensNext, savedAdditionalComment, form]);

    useEffect(() => {
        if (message && !savedClicked && customContentQuestion?.length) {
            dispatch(setContent(''));
            dispatch(toggleSpinner(false));
            navigate(`/impact-maker/content/final/additional-questions/${ID}`);
        } else if (message && !savedClicked && !customContentQuestion?.length) {
            dispatch(setContent(''));
            dispatch(toggleSpinner(false));
            navigate(`/impact-maker/content/final/preview/${ID}`);
        } else if (message && savedClicked) {
            dispatch(setContent(''));
            dispatch(toggleSpinner(false));
            navigate(`/impact-maker/content`);
        }
    }, [message, savedClicked, customContentQuestion, dispatch, navigate, ID]);

    useEffect(() => {
        if (opportunityID) {
            document?.documentElement?.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            dispatch(getContent({ contentID: ID }));
            dispatch(getOpportunity(opportunityID));
        }
    }, [opportunityID, dispatch, ID]);

    useEffect(() => {
        if (programID) {
            dispatch(getProgramById(programID));
        }
    }, [programID, dispatch]);

    const onFinishFailed = (errorInfo) => {
        console.log("Validation Failed:", errorInfo);
        const hasWhatHappensNextError = errorInfo.errorFields.some(
            (field) => field.name[0] === 'whatHappensNext'
        );
        const hasAdditionalCommentError = errorInfo.errorFields.some(
            (field) => field.name[0] === 'additionalComment'
        );
        setWhatHappensNextError(hasWhatHappensNextError);
        setAdditionalCommentError(hasAdditionalCommentError);
    };

    const handleQuillChangeWhatHappensNext = (content, delta, source, editor) => {
        const plainText = editor.getText().trim();
        const charCount = plainText.length;
        if (charCount <= maxChars) {
            setWhatHappensNext(content);
            form.setFieldsValue({ whatHappensNext: plainText.length > 0 ? content : '' });
            form.validateFields(['whatHappensNext']).then(() => {
                setWhatHappensNextError(false);
            }).catch(() => {
                setWhatHappensNextError(true);
            });
        }
    };

    const handleQuillChangeAdditionalComment = (content, delta, source, editor) => {
        const plainText = editor.getText().trim();
        const charCount = plainText.length;
        if (charCount <= maxChars) {
            setAdditionalComment(content);
            form.setFieldsValue({ additionalComment: plainText.length > 0 ? content : '' });
            form.validateFields(['additionalComment']).then(() => {
                setAdditionalCommentError(false);
            }).catch(() => {
                setAdditionalCommentError(true);
            });
        }
    };

    const handleProceed = () => {
        form.validateFields().then(values => {
            const data = {
                contentId: ID,
                whatHappensNext: formData?.whatHappensNext,
                additionalComment: formData?.additionalComment,
                action: "Draft",
                programID,
                opportunityID
            };
            dispatch(editContent(data));
            dispatch(toggleSpinner(true));
            setWhatHappensNextError(false);
            setAdditionalCommentError(false);
        }).catch(errorInfo => {
            console.log('Validation Failed:', errorInfo);
            const hasWhatHappensNextError = errorInfo.errorFields.some(
                (field) => field.name[0] === 'whatHappensNext'
            );
            const hasAdditionalCommentError = errorInfo.errorFields.some(
                (field) => field.name[0] === 'additionalComment'
            );
            setWhatHappensNextError(hasWhatHappensNextError);
            setAdditionalCommentError(hasAdditionalCommentError);
        });
    };

    const handleSaveAndExit = () => {
        setSavedClicked(true);
        form.validateFields().then(() => {
            form.submit();
            setWhatHappensNextError(false);
            setAdditionalCommentError(false);
        }).catch(errorInfo => {
            console.log('Validation Failed:', errorInfo);
            dispatch(toggleSpinner(false));
            const hasWhatHappensNextError = errorInfo.errorFields.some(
                (field) => field.name[0] === 'whatHappensNext'
            );
            const hasAdditionalCommentError = errorInfo.errorFields.some(
                (field) => field.name[0] === 'additionalComment'
            );
            setWhatHappensNextError(hasWhatHappensNextError);
            setAdditionalCommentError(hasAdditionalCommentError);
        });
    };

    const handlePrevious = () => {
        navigate(`/impact-maker/content/final/case-studies/${ID}`);
    };

    const onFormChange = (_, allValues) => {
        setFormData(allValues);
    };

    return (
        <div className={baseClassName}>
            {showSpinner && (
                <div className="overlay">
                    <Spin className="spinner" size="large" spinning={showSpinner} />
                </div>
            )}
            <Steps
                current={3}
                items={[
                    {
                        title: <StyledText as="p" variant="H3">{intl.formatMessage({ id: "step_1" })}</StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>{intl.formatMessage({ id: "inputs_n_outputs" })}</StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">{intl.formatMessage({ id: "step_2" })}</StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>{intl.formatMessage({ id: "objectives_n_outcomes" })}</StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">{intl.formatMessage({ id: "step_3" })}</StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>{intl.formatMessage({ id: "case_studies" })}</StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">{intl.formatMessage({ id: "step_4" })}</StyledText>,
                        description: <StyledText as="p" variant="B3c" className='mainTitle'>{intl.formatMessage({ id: "next_steps" })}</StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">{intl.formatMessage({ id: "step_5" })}</StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>{intl.formatMessage({ id: "preview_n_submit" })}</StyledText>,
                    },
                ]}
            />
            <div className='contentContainer'>
                <div>
                    <StyledText as="p" variant="H1" className='mainTitle'>{intl.formatMessage({ id: "next_steps" })}</StyledText>
                    <Form
                        {...formItemLayout}
                        labelAlign="left"
                        onFinish={handleProceed}
                        onFinishFailed={onFinishFailed}
                        form={form}
                        className="formContainer"
                        scrollToFirstError
                        onValuesChange={onFormChange}
                    >
                        <div className="mainContentContainer">
                            <div className="contentContainer">
                                <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "what_happens_next_for_example_are_you_planning_to_develop_your_project_further" })}</StyledText>
                                <Form.Item
                                    name="whatHappensNext"
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                const text = value && typeof value === 'string' ? value.replace(/<[^>]+>/g, '').trim() : '';
                                                if (text.length > 0) {
                                                    setWhatHappensNextError(false);
                                                    return Promise.resolve();
                                                }
                                                setWhatHappensNextError(true);
                                                return Promise.reject(new Error(intl.formatMessage({ id: "please_enter" })));
                                            }
                                        }
                                    ]}
                                >
                                    <ReactQuill
                                        theme="snow"
                                        value={whatHappensNext}
                                        onChange={handleQuillChangeWhatHappensNext}
                                        placeholder={intl.formatMessage({ id: "enter_description" })}
                                        modules={{
                                            toolbar: [
                                                ['bold', 'italic', 'underline'],
                                                ['link'],
                                                [{ list: 'ordered' }, { list: 'bullet' }],
                                                ['clean'],
                                            ],
                                        }}
                                        className={whatHappensNextError ? 'quill-error' : ''}
                                    />
                                </Form.Item>
                                <StyledText as="span" variant="B3d">
                                    {intl.formatMessage({ id: "if_you_would_like_to_share_anything_else_with_the_company_please_use_the_box_below" })}
                                    <StyledText as="span" variant="B3a">{` (${intl.formatMessage({ id: "optional" })})`}</StyledText>
                                </StyledText>
                                <Form.Item
                                    name="additionalComment"
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                setAdditionalCommentError(false);
                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                >
                                    <ReactQuill
                                        theme="snow"
                                        value={additionalComment}
                                        onChange={handleQuillChangeAdditionalComment}
                                        placeholder={intl.formatMessage({ id: "enter_description" })}
                                        modules={{
                                            toolbar: [
                                                ['bold', 'italic', 'underline'],
                                                ['link'],
                                                [{ list: 'ordered' }, { list: 'bullet' }],
                                                ['clean'],
                                            ],
                                        }}
                                        className={additionalCommentError ? 'quill-error' : ''}
                                    />
                                </Form.Item>
                            </div>
                            <div className="buttonContainer">
                                <Divider />
                                <div className="buttonContentContainer">
                                    <div className="nextAndPreviousContainer">
                                        <Button variant="secondary" type="button" onClick={handlePrevious}>
                                            {intl.formatMessage({ id: "previous" })}
                                        </Button>
                                        <Button variant="primary" htmlType="submit">
                                            {intl.formatMessage({ id: "next" })}
                                        </Button>
                                    </div>
                                    <div className="saveAndExitButton">
                                        <Button variant="secondary" htmlType="button" onClick={handleSaveAndExit}>
                                            {intl.formatMessage({ id: "save_n_exit" })}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
};

const StyledCharityFinalNextSteps = styled(CharityFinalNextSteps)`
  &.charityFinalNextSteps {
    background: white;
    display: flex;
    flex-direction: column;
    padding: 20px;
    justify-content: space-between;
    margin-top: 50px;

    .inputElement .ant-form-item-control-input {
        width: 200px;
    }

    .subContainer {
        display: flex;
        flex-direction: column;
        gap: 10px;
        .descriptionContainer {
            max-width: 70ch;
            word-wrap: break-word;
            overflow-wrap: break-word;
            p, li {
                font-size: 14px;
                font-weight: 500;
                color: #242424;
                font-family: sans-serif;
                max-width: 70ch;
                word-wrap: break-word;
                overflow-wrap: break-word;
            }
        }
    }

    .mainTitle {
        margin-bottom: 20px;
    }

    .contentContainer {
        display: flex;
        flex-direction: column;
        gap: 12px;
        min-height: 60vh;
        padding-bottom: 60px;
        overflow-y: scroll;
        .formContainer {
            display: flex;
            gap: 12px;
            flex-direction: column;
        }
    }

    .overlay {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 1000;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.45);
    }

    .spinner {
        z-index: 2000;
        margin: auto;
        left: 50%;
        right: 50%;
        position: absolute;
        bottom: 50%;
    }

    .formItemTextField {
        .ant-form-item-control-input {
            width: 34.5vw !important;
        }
    }

    .formItemTextFieldPart {
        .ant-form-item-control-input {
            width: 16vw !important;
        }
    }

    .ant-form-item {
        margin-bottom: 10px;
    }

    .formItemInput {
        width: 300px;
    }

    .buttonContainer {
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        position: fixed;
        width: 100%;
        bottom: 0px;
        background: white;
        margin-left: -110px;
        .buttonContentContainer {
            display: flex;
            justify-content: center;
            width: 100%;
            position: relative;
            .saveAndExitButton {
                position: absolute;
                right: 0;
                margin-bottom: 15px;
            }
            .nextAndPreviousContainer {
                display: flex;
                gap: 20px;
                margin-bottom: 15px;
            }
        }
    }

    .quill-error {
        border: 1px solid red !important;
    }
}
`;

export default StyledCharityFinalNextSteps;